import {HeaderItem, TableHeaderAction} from "@src/Pages/Views/tableTypes.ts";
import styles from "./eventsTable.module.less";
import React, {useMemo, useState} from "react";
import {getByPath} from "@src/utils/getByPath.ts";
import {DataNotFoundMessage, } from "@src/Pages/Views/Common.tsx";
import {Body_14_Coral_Primary_Regular_14} from "@UI/typography/generated/typography.tsx";
import cn from "classnames";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {ArrowChevronUp} from "@UI/components/icons/ArrowChevronUp.tsx";
import {ArrowChevronDown} from "@UI/components/icons/ArrowChevronDown.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {
    LocationLink,
} from "@src/Pages/Views/Manager/AttendanceHistory/DailyEvents/DailyEvents.tsx";
import {makeGroupEventsOffline} from "@src/Store/eventsStore.ts";
import {CheckInOutPair} from "@src/httpClient/__generated__/Api.ts";
import {getTimeFromDT} from "@src/utils/formatDate.ts";
import {EmployeeWithWorkdays} from "@src/Store/employeesStore.ts";


export const WorkTime = ({event, type}: { event: CheckInOutPair, type: "in" | "out" }) => {
    const noData = (
        <Body_14_Coral_Primary_Regular_14>
            —
        </Body_14_Coral_Primary_Regular_14>
    )
    const selected_event = type === "in" ? event?.event_in : event?.event_out;
    if (!selected_event) return noData
    return (
        <Body_14_Coral_Primary_Regular_14>
            {getTimeFromDT(selected_event?.recorded_at)}
        </Body_14_Coral_Primary_Regular_14>
    )
}

export const WorkCoords = ({event, type}: { event: CheckInOutPair, type: "in" | "out" }) => {
    const noData = (
        <Body_14_Coral_Primary_Regular_14>
            —:—
        </Body_14_Coral_Primary_Regular_14>
    )
    const selected_event = type === "in" ? event?.event_in : event?.event_out;
    if (!selected_event) return noData
    return <LocationLink event={selected_event}/>
}


export const headerItems: Array<HeaderItem> = [
    {
        dataLabel: "inv_one",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (_) => <div />
    },
    {
        dataLabel: "inv_two",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (_) => <div />
    },
    {
        label: <Gt>Work time</Gt>,
        dataLabel: "checkin_time",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => <WorkTime event={data} type={"in"} />
    },
    {
        label: <Gt>GPS in</Gt>,
        dataLabel: "checkin_coords",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => <WorkCoords event={data} type={"in"} />
    },
    {
        label: <Gt>Leave work time</Gt>,
        dataLabel: "checkout_time",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => <WorkTime event={data} type={"out"} />
    },
    {
        label: <Gt>GPS out</Gt>,
        dataLabel: "checkout_coords",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => <WorkCoords event={data} type={"out"} />
    },
    {
        dataLabel: "inv_last",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => <div />
    },
]


const TableHeader = ({headerColumns, expandable = false}: {
    headerColumns: Array<HeaderItem>,
    expandable: boolean
}) => {
    return (
        <div className={cn(styles.tableHeader, {[styles.expandable]: expandable})}>
            {headerColumns.map(column =>
                <div className={styles.tableCell} key={column.dataLabel}>
                    <Body_14_Coral_Primary_Regular_14>
                        {column.label}
                    </Body_14_Coral_Primary_Regular_14>
                </div>)
            }
            {expandable && <div className={styles.tableCell} aria-hidden="true"/>}
        </div>
    )
}

const TableRowValue = ({column, item}: { column: HeaderItem, item: EmployeeWithWorkdays }) => {
    const value = useMemo(() => {
        let _value = ""
        if (column?.paramKey === "self" && column?.renderer) {
            return column.renderer(item)
        }

        if (column.paramKeys) {
            for (const key of column.paramKeys) {
                _value += getByPath(item, key)
            }
        } else if (column.paramKey) {
            _value = getByPath(item, column.paramKey)
        }

        if (column?.renderer) {
            return column.renderer(_value)
        }
        return _value
    }, [column, item])

    if (!item) return null

    return (
        <div className={styles.tableCell} data-label={column.dataLabel}>{value}</div>
    )
}

const TableRow = ({headerColumns, item, expandable}: {
    headerColumns: Array<HeaderItem>,
    item: any,
    expandable: boolean
}) => {
    const [showDetail, setShowDetail] = useState(false)

    const groups = useMemo(() => {
        if (item?.workdays?.length === 0) return []
        const events = item?.workdays?.[0]?.events
        return makeGroupEventsOffline(events ?? [])
    }, [item])

    if (!item) return null
    return (
        <>
            <div className={cn(styles.tableRow, {[styles.expanded]: showDetail})}>
                {headerColumns.map(column =>
                    <TableRowValue column={column} item={item} key={`${column.dataLabel}-${item?.id}`}/>
                )}
                {expandable &&
                    groups?.length > 1
                    ?
                    <div className={cn(styles.tableCell, styles.buttonsColl)} data-label={"chevron"}>
                        <Button
                            color={ColorNames.OBSIDIAN}
                            type={ButtonType.clickableIcon}
                            onClick={() => setShowDetail(!showDetail)}
                            className={styles.buttonsColl}
                            icon={
                                showDetail
                                    ? <ArrowChevronUp size={24}/>
                                    : <ArrowChevronDown size={24}/>
                            }
                        />
                    </div>
                    :
                    <div className={cn(styles.tableCell, styles.buttonsColl)} data-label={"chevron"}/>
                }

            </div>
            {showDetail &&
                <div className={styles.detailRow}>
                    <div className={styles.detailInner}>
                        <DailyEventsTable headerColumns={headerItems} content={groups}/>
                    </div>
                </div>
            }
        </>
    )
}

const TableEventsRow = ({headerColumns, item}: {
    headerColumns: Array<HeaderItem>,
    item: any
}) => {
    const [showDetail, setShowDetail] = useState(false)

    if (!item) return null
    return (
        <>
            <div className={cn(styles.tableRow, {[styles.expanded]: showDetail})}>
                {headerColumns.map(column =>
                    <TableRowValue column={column} item={item} key={`${column.dataLabel}-${item?.id}`}/>
                )}
                            </div>
            {showDetail &&
                <div className={styles.detailRow}>
                    <div className={styles.detailInner}>
                        <DailyEventsTable headerColumns={headerItems} content={item}/>
                    </div>
                </div>
            }
        </>
    )
}

export const DailyEventsTable = ({headerColumns, content}: {
    headerColumns: Array<HeaderItem>,
    content?: CheckInOutPair[]
}) => {
    const cssVars = {"--cols": headerColumns.length} as React.CSSProperties & {
        ["--cols"]: number;
    };

    return (
        <div className={cn(styles.eventsTable, styles.lowBordered)} style={cssVars}>
            <TableHeader headerColumns={headerColumns} expandable={false} />
            <div className={cn(styles.tableBody)}>
                {content?.map(item =>
                    <TableEventsRow headerColumns={headerColumns} item={item} key={item.date} />
                )}
                {!content?.length && <DataNotFoundMessage/>}
            </div>
        </div>
    )
}

export const DailyWorkdaysTable = ({headerColumns, content}: {
    headerColumns: Array<HeaderItem>,
    content?: EmployeeWithWorkdays[]
}) => {
    const cssVars = {"--cols": headerColumns.length} as React.CSSProperties & {
        ["--cols"]: number;
    };

    return (
        <div className={styles.eventsTable} style={cssVars}>
            <TableHeader headerColumns={headerColumns} expandable={true}/>
            <div className={cn(styles.tableBody, styles.expandable)}>
                {content && content?.map(item =>
                    <TableRow headerColumns={headerColumns} item={item} key={item.id} expandable={true}/>
                )}
            </div>
            <div style={{backgroundColor: "white", padding: "16px"}}>
                {!content?.length && <DataNotFoundMessage/>}
            </div>
        </div>
    )
}